import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Card, Typography, Space, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts';
import authService from '../../services/auth.service';
import PageTitle from '../Layouts/PageTitle';

const { Title, Text } = Typography;

const VerificationCode: React.FC = () => {
  const [form] = Form.useForm();
  const { user, setVerified } = useAuth();
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [maskedEmail, setMaskedEmail] = useState('');
  const navigate = useNavigate();

  const codeRequested = useRef(false);

  useEffect(() => {
    // Only request verification code if we haven't already
    if (!codeRequested.current) {
      requestVerificationCode();
      codeRequested.current = true;
    }
  }, []);
  const requestVerificationCode = async () => {
    try {
      setLoading(true);
      const response = await authService.requestVerificationCode();
      setMaskedEmail(response.data.email);
      // Set 60-second countdown for resend button
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prevCount => {
          if (prevCount <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);
    } catch (error: any) {
      console.error('Failed to request verification code:', error);
      // The server caps codes per user per hour (lhphcapital-7xvj). Say so, or a refused
      // Resend looks like an email that never arrived.
      if (error?.response?.data?.code === 'MFA_TOO_MANY_CODES') {
        form.setFields([{ name: 'code', errors: [error.response.data.message] }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: { code: string }) => {
    try {
      setLoading(true);
      await authService.verifyCode(values.code);
      // Mark user as verified
      setVerified(true);
      // Navigate to home or dashboard
      navigate('/home/news-info');
    } catch (error: any) {
      console.error('Code verification failed:', error);
      // After too many wrong codes the server burns the code: retrying it can't work,
      // so say to request a new one (the Resend button below).
      const burned = error?.response?.data?.code === 'MFA_TOO_MANY_ATTEMPTS';
      form.setFields([
        {
          name: 'code',
          errors: [
            burned
              ? 'Too many incorrect codes. Please use Resend to get a new code.'
              : 'Invalid verification code. Please try again.',
          ],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTitle title="Verification">
      <div style={{ maxWidth: '400px', width: '100%', margin: '50px auto' }}>
        <Card>
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={3}>Verification Required</Title>
              <Text>We've sent a 6-digit verification code to {maskedEmail}</Text>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item
                name="code"
                rules={[
                  { required: true, message: 'Please enter verification code' },
                  { len: 6, message: 'Code must be 6 digits' },
                ]}
              >
                <Input
                  placeholder="Enter 6-digit code"
                  size="large"
                  style={{ textAlign: 'center', letterSpacing: '2px', fontSize: '20px' }}
                  maxLength={6}
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" size={'large'} htmlType="submit" loading={loading} block>
                  Verify
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center' }}>
              <Text>Didn't receive the code? </Text>
              <Button
                size={'small'}
                type="link"
                // Also while a send is in flight: two sends at once issue two codes, and
                // only the newer one verifies.
                disabled={countdown > 0 || loading}
                onClick={requestVerificationCode}
              >
                Resend {countdown > 0 ? `(${countdown}s)` : ''}
              </Button>
            </div>

            <div style={{ textAlign: 'center' }}>
              <Button
                type="link"
                onClick={() => navigate('/sign-in')}
                style={{ padding: 0, height: 'auto', fontSize: '14px' }}
              >
                ← Back to login
              </Button>
            </div>
          </Space>
        </Card>
      </div>
    </PageTitle>
  );
};

export default VerificationCode;
